import React, { useState } from 'react';
import { GoodsCarousel, type GoodsData } from '@banhala/ui-kit';
import {
  bridge,
  isNotNil,
  useNavigator,
  getRangeForEvent,
} from 'shared/src/utils';
import { useLogDisplayGoods } from '../../../../../hooks/useLogDisplayGoods';
import { useAddLikeGoods } from 'shared/src/hooks';
import type { MarketHomeGoodsItem } from 'shared/src/api/marketService';
import { useGetMarketAnalyticsDefaults } from '../../../hooks/useLogMarketAnalyticsEvent';
import { useSyncLocalGoodsLike } from 'shared/src/hooks/useSyncLocalGoodsLike';
import { useRemoveLikeGoods } from 'shared/src/queries';

export const GoodsCarouselListView: React.FC<{
  list: (MarketHomeGoodsItem & GoodsData)[];
  listId: string;
  listParams?: string;
  marketId: number;
}> = props => {
  const { logDisplayGoods } = useLogDisplayGoods({
    list_params: props.listParams,
    list_id: props.listId,
  });

  const [likeMap, setLikeMap] = useState<{ [key: number]: boolean }>(() =>
    props.list.reduce((acc, val) => {
      return {
        ...acc,
        [val.item.sno]: isNotNil(val.item.like),
      };
    }, {}),
  );

  const getDefaultMarketAnalytics = useGetMarketAnalyticsDefaults(
    props.marketId,
  );
  const { addLikeGoods } = useAddLikeGoods({
    listId: props.listId,
    listParams: props.listParams,
    onChangeIsLiked: (sno, nextValue) => {
      setLikeMap(prevState => ({ ...prevState, [sno]: nextValue }));
    },
    screen: 'MARKET',
  });

  const { mutateAsync: mutateRemoveLikeGoods } = useRemoveLikeGoods();
  useSyncLocalGoodsLike(setLikeMap);
  const { navigator } = useNavigator();

  return (
    <GoodsCarousel<MarketHomeGoodsItem>
      list={props.list}
      likeMap={likeMap}
      initialNumToRender={4}
      listTestID={props.listId}
      windowSize={4}
      showRanking={props.list.some(item => isNotNil(item.display.ranking))}
      onPressItem={async (pressed, index) => {
        const { item, render, logging } = pressed;
        bridge.logAnalyticsEvent({
          name: 'CLICK_GOODS',
          analytics: {
            SCREEN: 'MARKET',
            CATEGORY_DEPTH: item.category?.depth,
            CATEGORY_NAME: item.category?.name,
            CATEGORY_SNO: item.category?.sno.toString(),
            DELIVERY_TYPE: item.delivery_type,
            DISCOUNT_RATE: item.discount_rate,
            GOODS_NAME: item.name,
            GOODS_SNO: logging['click'].goods_sno.toString(),
            IMAGE_BADGE_LIST: render.data.image_badge_list.map(
              ({ type }) => type,
            ),
            IS_AD: render.data.has_ad_badge ? '1' : '0',
            IS_NEW: item.is_new ? '1' : '0',
            // item.likes_count가 있으면 item.likes_count를 사용해야한다.
            // 해당 페이지는 item.likes_count가 없어서 null로 처리
            LIKES_COUNT: null,
            // item.likes_count로 500 단위로 끊어서 전송 (2,001~2,500)
            // 해당 페이지는 item.likes_count가 없어서 null로 처리
            LIKES_COUNT_RANGE: null,
            LIST_ID: logging['click'].list_id,
            LIST_INDEX: logging['click'].list_index,
            LIST_PARAMS: props.listParams,
            MARKET_NAME: item.market.name,
            MARKET_SNO: item.market.sno.toString(),
            MARKET_TYPE: item.market.market_type.name,
            ORIGINAL_PRICE:
              item.first_page_rendering?.original_price ??
              item.linked_option.original_price,
            REVIEW_COUNT: item.total_review_count,
            REVIEW_COUNT_RANGE: getRangeForEvent({
              target: item.total_review_count,
              rangeUnit: 50,
            }),
            REVIEW_RATING: item.positive_review_rate,
            SALES_PRICE: item.price,
            SALES_PRICE_RANGE: getRangeForEvent({
              target: item.price,
              rangeUnit: 5000,
            }),
            STANDARD_CATEGORY_NAME: item.standard_category.name,
            STANDARD_CATEGORY_SNO: item.standard_category.sno.toString(),
            MARKET_TYPE_SNO: item.market.market_type.sno.toString(),
            INDEX: index,
          },
        });
        navigator.moveToGoods({
          sno: item.sno,
          firstPageRendering: {
            discount_rate: item.first_page_rendering.discount_rate,
            goods_name: item.first_page_rendering.goods_name,
            original_price: item.first_page_rendering.original_price,
            price: item.first_page_rendering.price,
            cover_image: item.first_page_rendering.cover_image,
            market_image: undefined,
            market_name: item.first_page_rendering.market_name ?? undefined,
          },
        });
      }}
      onPressLike={async (pressed, index) => {
        const { item, render, logging } = pressed;

        const analytics = {
          SCREEN: 'MARKET',
          CATEGORY_DEPTH: item.category?.depth,
          CATEGORY_NAME: item.category?.name,
          CATEGORY_SNO: item.category?.sno.toString(),
          DELIVERY_TYPE: item.delivery_type,
          DISCOUNT_RATE: item.discount_rate,
          GOODS_NAME: item.name,
          GOODS_SNO:
            logging[
              !likeMap[item.sno] ? 'like' : 'unlike'
            ].goods_sno.toString(),
          IMAGE_BADGE_LIST: render.data.image_badge_list.map(
            ({ type }) => type,
          ),
          IS_AD: render.data.has_ad_badge ? '1' : '0',
          IS_NEW: item.is_new ? '1' : '0',
          // item.likes_count가 있으면 item.likes_count를 사용해야한다.
          // 해당 페이지는 item.likes_count가 없어서 null로 처리
          LIKES_COUNT: null,
          // item.likes_count로 500 단위로 끊어서 전송 (2,001~2,500)
          // 해당 페이지는 item.likes_count가 없어서 null로 처리
          LIKES_COUNT_RANGE: null,
          LIST_ID: logging[!likeMap[item.sno] ? 'like' : 'unlike'].list_id,
          LIST_INDEX:
            logging[!likeMap[item.sno] ? 'like' : 'unlike'].list_index,
          // LIST_PARAMS: props.syncedInfiniteRecentlyViewedGoods.list_params,
          MARKET_NAME: item.market.name,
          MARKET_SNO: item.market.sno.toString(),
          MARKET_TYPE: item.market.market_type.name,
          ORIGINAL_PRICE:
            item.first_page_rendering?.original_price ??
            item.linked_option.original_price,
          REVIEW_COUNT: item.total_review_count,
          REVIEW_COUNT_RANGE: getRangeForEvent({
            target: item.total_review_count,
            rangeUnit: 50,
          }),
          REVIEW_RATING: item.positive_review_rate,
          SALES_PRICE: item.price,
          SALES_PRICE_RANGE: getRangeForEvent({
            target: item.price,
            rangeUnit: 5000,
          }),
          STANDARD_CATEGORY_NAME: item.standard_category.name,
          STANDARD_CATEGORY_SNO: item.standard_category.sno.toString(),
          MARKET_TYPE_SNO: item.market.market_type.sno.toString(),
          INDEX: index,
        };

        if (!likeMap[item.sno]) {
          bridge.logAnalyticsEvent({
            name: 'CLICK_LIKE_GOODS',
            analytics,
          });
          // 좋아요 누르기
          addLikeGoods(item.sno, {
            SCREEN: 'MARKET',
            ...getDefaultMarketAnalytics()?.analytics,
          });
          return;
        }

        bridge.logAnalyticsEvent({
          name: 'CLICK_UNLIKE_GOODS',
          analytics,
        });

        await mutateRemoveLikeGoods({
          sno: item.sno,
          list_params_referrer: props.listParams,
        });

        //해제하기
        bridge.likeGoods({
          goods_sno: item.sno,
          is_liked: false,
        });
        setLikeMap(prevState => ({ ...prevState, [item.sno]: false }));
      }}
      size={'medium'}
      onViewableItemsChanged={logDisplayGoods}
    />
  );
};
